<template>
  <div>
    <v-dialog
      :value="value"
      max-width="480"
      persistent
      @input="$emit('input', $event)"
    >
      <v-card v-if="form">
        <v-card-title class="font-weight-medium secondary--text primary">
          <v-icon small color="secondary" class="mr-2">mdi-pencil</v-icon>
          Edit Entry
          <v-spacer />
          <v-btn small icon @click="close">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pt-5">
          <v-form ref="editForm" lazy-validation>
            <v-row dense>
              <v-col cols="12">
                <v-menu
                  v-model="editDatePicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formattedEditDate"
                      label="Webinar Date"
                      outlined
                      dense
                      hide-details="auto"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      prepend-inner-icon="mdi-calendar"
                    />
                  </template>
                  <v-date-picker
                    v-model="form.webinar_date"
                    :min="minDate"
                    @input="editDatePicker = false"
                  />
                </v-menu>
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="form.slot"
                  label="Slot (e.g. S1, S2)"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="form.faculty_name"
                  label="Faculty Name"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>

              <v-col cols="6">
                <v-menu
                  v-model="fromTimePicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="form.from_time"
                      label="From Time"
                      outlined
                      dense
                      hide-details="auto"
                      readonly
                      clearable
                      v-bind="attrs"
                      v-on="on"
                      prepend-inner-icon="mdi-clock-outline"
                      placeholder="09:00"
                      :error="submitted && !!timeError"
                      @click:clear="form.from_time = null"
                    />
                  </template>
                  <v-time-picker
                    v-if="fromTimePicker"
                    v-model="form.from_time"
                    ampm-in-title
                    full-width
                    @click:minute="onFromTimeSet"
                  />
                </v-menu>
              </v-col>

              <v-col cols="6">
                <v-menu
                  v-model="toTimePicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="form.to_time"
                      label="To Time"
                      outlined
                      dense
                      hide-details="auto"
                      readonly
                      clearable
                      v-bind="attrs"
                      v-on="on"
                      prepend-inner-icon="mdi-clock-outline"
                      placeholder="10:00"
                      :error="submitted && !!timeError"
                      :disabled="!form.from_time"
                      @click:clear="form.to_time = null"
                    />
                  </template>
                  <v-time-picker
                    v-if="toTimePicker"
                    v-model="form.to_time"
                    ampm-in-title
                    full-width
                    :min="form.from_time"
                    @click:minute="toTimePicker = false"
                  />
                </v-menu>
              </v-col>

              <v-col cols="12" v-if="submitted && timeError">
                <div class="caption error--text">{{ timeError }}</div>
              </v-col>

              <v-col cols="12">
                <v-text-field
                  v-model="form.webinar_link"
                  label="Webinar Link"
                  outlined
                  dense
                  hide-details="auto"
                  prepend-inner-icon="mdi-link"
                />
              </v-col>

              <v-col cols="12" v-if="isScheduled">
                <v-switch
                  v-model="form.is_postponed"
                  label="Postponed"
                  color="red"
                  hide-details="auto"
                  class="mt-1"
                />
              </v-col>
              <v-col cols="12" v-else>
                <div class="caption grey--text mt-1">
                  These webinars have not been scheduled yet, so edits are not
                  recorded as a reschedule or postponement.
                </div>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider />
        <v-card-actions class="px-4 pb-4 pt-3">
          <v-spacer />
          <v-btn
            class="primary white--text"
            :loading="saving"
            @click="saveEntry"
          >
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="rescheduleConfirmationDialog" width="500" persistent>
      <confirmationDialog
        v-if="rescheduleConfirmationDialog"
        confirmationText="Are you sure you want to reschedule this webinar?"
        :loading="saving"
        @closeDialog="rescheduleConfirmationDialog = false"
        @saveDialog="confirmReschedule"
      />
    </v-dialog>
  </div>
</template>

<script>
import confirmationDialog from "@/components/confirmationDialog.vue";

export default {
  name: "EditEntryDialog",
  components: { confirmationDialog },
  props: {
    value: { type: Boolean, default: false },
    item: { type: Object, default: null },
    minDate: { type: String, default: "" },
    scheduled: { type: Boolean, default: false },
  },
  data() {
    return {
      form: null,
      originalItem: null,
      editDatePicker: false,
      fromTimePicker: false,
      toTimePicker: false,
      saving: false,
      submitted: false,
      rescheduleConfirmationDialog: false,
    };
  },
  computed: {
    isScheduled() {
      return this.scheduled;
    },
    formattedEditDate() {
      if (!this.form?.webinar_date) return "";
      const [y, m, d] = this.form.webinar_date.split("-");
      return `${d}-${m}-${y}`;
    },
    timeError() {
      if (!this.form) return "";
      const { from_time, to_time } = this.form;
      if (!from_time && !to_time) {
        return "From Time and To Time are required";
      }
      if (!from_time) {
        return "Please set From Time first";
      }
      if (!to_time) {
        return "To Time is required";
      }
      const normFrom = this.normalizeScheduleValue(from_time, "from_time");
      const normTo = this.normalizeScheduleValue(to_time, "to_time");
      if (!this.isValidTime(normFrom) || !this.isValidTime(normTo)) {
        return "Time must be in HH:MM format";
      }
      if (normTo === normFrom) {
        return "To Time must be after From Time";
      }
      if (normTo < normFrom) {
        return "To Time cannot be earlier than From Time";
      }
      return "";
    },
  },
  watch: {
    value(open) {
      if (open) this.initForm();
    },
  },
  methods: {
    initForm() {
      const item = this.item || {};
      this.form = {
        ...item,
        from_time: item.from_time ? this.formatTime(item.from_time) : item.from_time,
        to_time: item.to_time ? this.formatTime(item.to_time) : item.to_time,
        faculty_name: this.toText(item.faculty_name),
        webinar_link: this.toText(item.webinar_link),
      };
      this.originalItem = { ...item };
      this.fromTimePicker = false;
      this.toTimePicker = false;
      this.submitted = false;
    },

    // An entry holds one faculty name and one link. Older payloads may still
    // arrive as a one-element array, so unwrap before showing it.
    toText(value) {
      const raw = Array.isArray(value) ? value[0] : value;
      return raw == null ? "" : String(raw).trim();
    },

    onFromTimeSet() {
      this.fromTimePicker = false;
      if (this.form.to_time && this.form.from_time) {
        const normTo = this.normalizeScheduleValue(this.form.to_time, "to_time");
        const normFrom = this.normalizeScheduleValue(
          this.form.from_time,
          "from_time",
        );
        if (normTo <= normFrom) {
          this.form.to_time = null;
        }
      }
    },

    async saveEntry() {
      this.submitted = true;
      if (this.timeError) {
        this.$store.commit("showSnackbar", {
          message: this.timeError,
          color: "error",
        });
        return;
      }
      if (this.isScheduled && this.hasScheduleChanged()) {
        this.rescheduleConfirmationDialog = true;
        return;
      }
      await this.updateEntry(false);
    },

    async confirmReschedule() {
      await this.updateEntry(true);
    },

    async updateEntry(markRescheduled) {
      this.saving = true;
      try {
        const payload = {
          webinar_date: this.form.webinar_date,
          slot: this.form.slot,
          from_time: this.form.from_time
            ? String(this.form.from_time).split(":").slice(0, 2).join(":")
            : null,
          to_time: this.form.to_time
            ? String(this.form.to_time).split(":").slice(0, 2).join(":")
            : null,
          webinar_link: this.toText(this.form.webinar_link),
          faculty_name: this.toText(this.form.faculty_name),
          is_postponed: this.isScheduled && Boolean(this.form.is_postponed),
        };
        if (this.isScheduled && markRescheduled) {
          payload.rescheduled = true;
        }
        await this.$axios.patch(`/scheduler/entries/${this.form.id}`, payload);
        this.$store.commit("showSnackbar", {
          message: markRescheduled ? "Entry rescheduled" : "Entry updated",
          color: "success",
        });
        this.rescheduleConfirmationDialog = false;
        this.$emit("saved");
        this.close();
      } catch (e) {
        const msg =
          e.response?.data?.error ||
          e.response?.data?.message ||
          "Update failed";
        this.$store.commit("showSnackbar", { message: msg, color: "error" });
      } finally {
        this.saving = false;
      }
    },

    hasScheduleChanged() {
      if (!this.form || !this.originalItem) return false;
      return ["webinar_date", "from_time", "to_time", "slot"].some((field) => {
        const originalRaw = this.originalItem[field];
        const currentRaw = this.form[field];
        const original = this.normalizeScheduleValue(originalRaw, field);
        const current = this.normalizeScheduleValue(currentRaw, field);
        if (
          (originalRaw == null || originalRaw === "") &&
          currentRaw != null &&
          currentRaw !== ""
        ) {
          return false;
        }
        return original !== current;
      });
    },

    isValidTime(value) {
      return /^([01]\d|2[0-3]):[0-5]\d$/.test(String(value));
    },

    normalizeScheduleValue(value, field) {
      if (value == null) return "";
      if (["from_time", "to_time"].includes(field)) {
        return String(value).split(":").slice(0, 2).join(":");
      }
      return String(value);
    },

    formatTime(timeStr) {
      if (!timeStr) return "—";
      return timeStr.split(":").slice(0, 2).join(":");
    },

    close() {
      this.$emit("input", false);
    },
  },
};
</script>
